// A game's metadata, by hand (F-CAT-01, F-CAT-07, F-HQ-16; spec/14 §6): edit a mirrored game, or add one the metadata
// provider does not know (or while it is down). Every field HQ changes here is **kept through refreshes** — the weekly
// refresh from IGDB, a re-import and Refresh leave it exactly as written — and the dialog lists those fields so HQ can
// hand one back to IGDB. Only the fields actually changed are sent, so opening and saving never pins a field.
//
// `requiresElevation` (spec/11 §3.5) is a flag, not metadata: stations launch the title through the elevated service,
// and a venue may turn it off for itself. **Archive** hides the title from venues adding titles; the venues that
// already offer it keep it, and the dialog says how many before HQ confirms (an audited count, read only on request).

import type { Game, GameEditableField, UpdateCatalogueGame } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, Field, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Notice } from '../../ui';
import { useCreateGame, useGameUsage, useSetGameArchived, useUpdateGame } from './queries';
import { useProblem } from '../../api/use-problem';

/** Labels (genres, tags) as HQ types them: comma-separated, blanks dropped. The server de-duplicates. */
export function parseLabels(text: string): string[] {
  return text
    .split(',')
    .map((label) => label.trim())
    .filter((label) => label !== '');
}

/** The editor's draft: every field a string, as the inputs hold it. */
export interface GameDraft {
  name: string;
  summary: string;
  genres: string;
  franchise: string;
  firstReleaseYear: string;
  publisher: string;
  ageRating: string;
  tags: string;
  requiresElevation: boolean;
}

export function gameDraftOf(game: Game | undefined): GameDraft {
  return {
    name: game?.name ?? '',
    summary: game?.summary ?? '',
    genres: (game?.genres ?? []).join(', '),
    franchise: game?.franchise ?? '',
    firstReleaseYear: game?.firstReleaseYear === null || game?.firstReleaseYear === undefined ? '' : String(game.firstReleaseYear),
    publisher: game?.publisher ?? '',
    ageRating: game?.ageRating ?? '',
    tags: (game?.tags ?? []).join(', '),
    requiresElevation: game?.requiresElevation ?? false,
  };
}

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

/** A typed year, or `null` for blank, or `undefined` when it is not a year the api takes (1950–2100). */
export function parseYear(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const year = Number(text.trim());
  return Number.isInteger(year) && year >= 1950 && year <= 2100 ? year : undefined;
}

/** What changed between the game and the draft, as the PATCH body — only fields that differ. */
export function gamePatchOf(game: Game, draft: GameDraft, handBack: readonly GameEditableField[]): UpdateCatalogueGame {
  const before = gameDraftOf(game);
  const patch: Record<string, unknown> = {};
  if (draft.name.trim() !== before.name) patch.name = draft.name.trim();
  if (draft.summary.trim() !== before.summary) patch.summary = orNull(draft.summary);
  if (parseLabels(draft.genres).join('\u0000') !== game.genres.join('\u0000')) patch.genres = parseLabels(draft.genres);
  if (draft.franchise.trim() !== before.franchise) patch.franchise = orNull(draft.franchise);
  if (draft.firstReleaseYear.trim() !== before.firstReleaseYear) patch.firstReleaseYear = parseYear(draft.firstReleaseYear) ?? null;
  if (draft.publisher.trim() !== before.publisher) patch.publisher = orNull(draft.publisher);
  if (draft.ageRating.trim() !== before.ageRating) patch.ageRating = orNull(draft.ageRating);
  if (parseLabels(draft.tags).join('\u0000') !== game.tags.join('\u0000')) patch.tags = parseLabels(draft.tags);
  if (draft.requiresElevation !== game.requiresElevation) patch.requiresElevation = draft.requiresElevation;
  if (handBack.length > 0) patch.providerFields = [...handBack];
  return patch as UpdateCatalogueGame;
}

export function GameDialog({ game, open, onOpenChange }: { readonly game?: Game; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      dirty="auto"
      title={game === undefined ? t('catalogue.game.createTitle') : t('catalogue.game.editTitle')}
      description={game?.name ?? t('catalogue.game.createIntro')}
    >
      {open ? <GameForm game={game} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function GameForm({ game, onDone }: { readonly game: Game | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const create = useCreateGame();
  const update = useUpdateGame();
  const [draft, setDraft] = useState<GameDraft>(() => gameDraftOf(game));
  const [handBack, setHandBack] = useState<GameEditableField[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [yearError, setYearError] = useState<string | undefined>(undefined);
  const set = (patch: Partial<GameDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const submit = () => {
    setError(undefined);
    const name = draft.name.trim();
    const year = parseYear(draft.firstReleaseYear);
    setNameError(name === '' ? t('catalogue.game.nameRequired') : undefined);
    setYearError(year === undefined ? t('catalogue.game.yearInvalid') : undefined);
    if (name === '' || year === undefined) return;
    const done = (saved: Game) => {
      show({ tone: 'ok', title: game === undefined ? t('catalogue.game.created', { game: saved.name }) : t('catalogue.game.saved', { game: saved.name }) });
      onDone();
    };
    const failed = (err: unknown) => setError(problems.text(err, { fallback: t('catalogue.game.failed') }));
    if (game === undefined) {
      create.mutate(
        {
          name,
          summary: orNull(draft.summary),
          genres: parseLabels(draft.genres),
          franchise: orNull(draft.franchise),
          firstReleaseYear: year,
          publisher: orNull(draft.publisher),
          ageRating: orNull(draft.ageRating),
          tags: parseLabels(draft.tags),
          requiresElevation: draft.requiresElevation,
        },
        { onSuccess: done, onError: failed },
      );
      return;
    }
    const body = gamePatchOf(game, draft, handBack);
    if (Object.keys(body).length === 0) {
      onDone();
      return;
    }
    update.mutate({ id: game.id, body }, { onSuccess: done, onError: failed });
  };

  return (
    <div className="console-form">
      <p className="console-muted">{game === undefined ? t('catalogue.game.createHint') : t('catalogue.game.intro')}</p>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <TextField label={t('catalogue.game.name')} value={draft.name} error={nameError} onChange={(event) => set({ name: event.target.value })} />
      <Field label={t('catalogue.game.summary')} showOptional>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            className="clav-input console-game-dialog__summary"
            rows={4}
            maxLength={4000}
            value={draft.summary}
            onChange={(event) => set({ summary: event.target.value })}
          />
        )}
      </Field>
      <TextField label={t('catalogue.game.genres')} description={t('catalogue.game.labelsHint')} showOptional value={draft.genres} onChange={(event) => set({ genres: event.target.value })} />
      <div className="console-game-dialog__pair">
        <TextField label={t('catalogue.game.publisher')} showOptional value={draft.publisher} onChange={(event) => set({ publisher: event.target.value })} />
        <TextField label={t('catalogue.game.ageRating')} description={t('catalogue.game.ageRatingHint')} showOptional value={draft.ageRating} onChange={(event) => set({ ageRating: event.target.value })} />
      </div>
      <div className="console-game-dialog__pair">
        <TextField label={t('catalogue.game.franchise')} showOptional value={draft.franchise} onChange={(event) => set({ franchise: event.target.value })} />
        <TextField
          label={t('catalogue.game.releaseYear')}
          showOptional
          numeric
          inputMode="numeric"
          value={draft.firstReleaseYear}
          error={yearError}
          onChange={(event) => set({ firstReleaseYear: event.target.value })}
        />
      </div>
      <TextField label={t('catalogue.game.tags')} description={t('catalogue.game.tagsHint')} showOptional value={draft.tags} onChange={(event) => set({ tags: event.target.value })} />
      <div className="console-game-dialog__flag">
        <Switch label={t('catalogue.game.elevation')} stateLabel checked={draft.requiresElevation} onCheckedChange={(requiresElevation) => set({ requiresElevation })} />
        <p className="console-muted">{t('catalogue.game.elevationHint')}</p>
      </div>

      {game !== undefined && game.editedFields.length > 0 ? (
        <fieldset className="console-game-dialog__edited">
          <legend className="clav-field__label">{t('catalogue.game.edited')}</legend>
          <p className="console-muted">{t('catalogue.game.editedHint')}</p>
          <div className="console-game-dialog__edited-list">
            {game.editedFields.map((field) => (
              <Checkbox
                key={field}
                label={t('catalogue.game.handBack', { field: t(`catalogue.game.field.${field}`) })}
                checked={handBack.includes(field)}
                onCheckedChange={(checked) => setHandBack((current) => (checked === true ? [...current, field] : current.filter((held) => held !== field)))}
              />
            ))}
          </div>
        </fieldset>
      ) : null}

      <div className="console-form__actions">
        <span className="console-game-dialog__spring" />
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('catalogue.game.cancel')}
        </Button>
        <Button type="button" variant="primary" busy={create.isPending || update.isPending} onClick={submit}>
          {game === undefined ? t('catalogue.game.create') : t('catalogue.game.save')}
        </Button>
      </div>

      {game === undefined ? null : <ArchiveSection game={game} onDone={onDone} />}
    </div>
  );
}

/** Archive or bring back: the venues offering it are counted only when HQ asks (an audited read). */
function ArchiveSection({ game, onDone }: { readonly game: Game; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const archive = useSetGameArchived();
  const [confirming, setConfirming] = useState(false);
  const usage = useGameUsage(game.id, confirming);
  const archived = game.archivedAt !== null;

  const change = (next: boolean) =>
    archive.mutate(
      { id: game.id, archived: next },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: next ? t('catalogue.game.archive.done', { game: game.name }) : t('catalogue.game.archive.undone', { game: game.name }) });
          onDone();
        },
        onError: (err) => show({ tone: 'danger', title: problems.text(err, { fallback: t('catalogue.game.archive.failed', { game: game.name }) }) }),
      },
    );

  let body: ReactNode;
  if (archived) {
    body = (
      <>
        <p className="console-muted">{t('catalogue.game.archive.isArchived', { date: new Date(game.archivedAt ?? '').toLocaleDateString() })}</p>
        <Button type="button" variant="secondary" busy={archive.isPending} onClick={() => change(false)}>
          {t('catalogue.game.archive.unarchive')}
        </Button>
      </>
    );
  } else if (!confirming) {
    body = (
      <>
        <p className="console-muted">{t('catalogue.game.archive.intro')}</p>
        <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
          {t('catalogue.game.archive.start')}
        </Button>
      </>
    );
  } else {
    body = (
      <>
        {usage.data === undefined ? (
          <p className="console-muted" role="status">
            {usage.isError ? t('catalogue.game.archive.usageFailed') : t('catalogue.game.archive.usageLoading')}
          </p>
        ) : (
          <Notice tone={usage.data.venues > 0 ? 'warn' : 'info'}>{t('catalogue.game.archive.usage', { count: usage.data.venues })}</Notice>
        )}
        <div className="console-form__actions">
          <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
            {t('catalogue.game.cancel')}
          </Button>
          <Button type="button" variant="danger" busy={archive.isPending} disabled={usage.data === undefined && !usage.isError} onClick={() => change(true)}>
            {t('catalogue.game.archive.confirm')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <section className="console-game-dialog__archive" aria-label={t('catalogue.game.archive.title')}>
      <h3>{t('catalogue.game.archive.title')}</h3>
      {body}
    </section>
  );
}
